import { Upload, Download, Play, Square, User, Sparkles, FolderOpen, Info, Globe, Image, Command, ChevronDown, MoreHorizontal, Plus, LayoutTemplate, History, BookOpen, HelpCircle } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { useStore } from '../store/useStore'
import { syncToP5js } from '../services/p5sync'
import { showToast } from './ui/Toast'
import { downloadSketch } from '../utils/downloadSketch'
import { haptic } from '../utils/haptics'
import { P5_VERSIONS } from '../config/p5versions'
import SketchBrowser from './SketchBrowser'
import PublishModal from './PublishModal'
import TemplateModal from './TemplateModal'
import ModeToggle from './ModeToggle'
import SocialRoomPicker from './SocialRoomPicker'
import MySketchesPanel from './MySketchesPanel'

export default function Header({ onAuthClick, onAboutClick, onChangelogClick, onHelpClick }) {
  const [showBrowser, setShowBrowser] = useState(false)
  const [showPublish, setShowPublish] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showRoomPicker, setShowRoomPicker] = useState(false)
  const [showMySketches, setShowMySketches] = useState(false)
  const { 
    isAuthenticated, 
    user, 
    sketchName, 
    isSaved,
    p5Credentials,
    p5Version,
    setP5Version,
    toggleAiPanel,
    showAiPanel
  } = useStore()

  const p5Versions = P5_VERSIONS

  const handleSync = useCallback(async () => {
    // Get fresh state to avoid stale closure when called via event listener
    const { isAuthenticated: isAuth, p5Credentials: creds } = useStore.getState()
    if (!isAuth || !creds) {
      showToast('Please login to sync with editor.p5js.org', 'error')
      onAuthClick()
      return
    }

    try {
      showToast('Syncing to editor.p5js.org...', 'info')
      const result = await syncToP5js()
      
      if (result.success) {
        showToast(`Synced successfully! View at editor.p5js.org`, 'success')
        useStore.getState().setSaved(true)
        useStore.getState().setSketchInfo({
          id: result.sketchId,
          name: result.sketchName
        })
      } else {
        showToast(result.error || 'Failed to sync', 'error')
      }
    } catch (error) {
      showToast('Sync failed: ' + error.message, 'error')
    }
  }, [onAuthClick])

  useEffect(() => {
    window.addEventListener('syncSketch', handleSync)
    return () => {
      window.removeEventListener('syncSketch', handleSync)
    }
  }, [handleSync])

  const handleRun = () => {
    haptic('medium')
    window.dispatchEvent(new CustomEvent('runSketch'))
    showToast('Running sketch...', 'info')
  }

  const handleStop = () => {
    haptic('light')
    window.dispatchEvent(new CustomEvent('stopSketch'))
    showToast('Sketch stopped', 'info')
  }

  const handleDownload = () => {
    haptic('success')
    const { files, sketchName } = useStore.getState()
    downloadSketch(sketchName, files)
    showToast('Sketch downloaded', 'success')
  }

  return (
    <header className="h-12 flex items-center justify-between px-3 bg-ide-surface border-b border-ide-border">
      <div className="flex items-center gap-3">
        <h1 className="text-sm font-semibold text-ide-text tracking-tight">p5js AI Cloud IDE</h1>
        <div className="h-4 w-px bg-ide-border" />
        
        {/* New from Template */}
        <button
          type="button"
          onClick={() => {
            haptic('light')
            setShowTemplates(true)
          }}
          className="flex items-center gap-1 px-2 py-1 text-ide-accent bg-ide-accent/10 border border-ide-accent/30 rounded-md hover:bg-ide-accent/20 transition text-xs font-medium"
          title="New from Template"
          data-tour="templates"
        >
          <LayoutTemplate size={12} />
          <span className="hidden md:inline">Templates</span>
        </button>
        
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={sketchName}
            onChange={(e) => useStore.getState().setSketchName(e.target.value)}
            className="bg-ide-bg border border-ide-border text-ide-text px-2.5 py-1 rounded-md text-sm outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30 transition w-40"
            placeholder="Sketch Name"
            aria-label="Sketch Name"
            title="Click to edit sketch name"
          />
          {!isSaved && <span className="text-ide-warning text-xs" title="Unsaved changes">●</span>}
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {/* Primary actions: Run/Stop */}
        <button
          type="button"
          onClick={handleRun}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-ide-accent text-white rounded-md hover:bg-ide-accentHover text-sm font-medium transition active:scale-[0.98]"
          title="Run Sketch (Ctrl+Enter)"
          aria-label="Run Sketch"
          data-tour="run-button"
        >
          <Play size={14} fill="currentColor" />
          <span>Run</span>
        </button>

        <button
          type="button"
          onClick={handleStop}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-ide-surface text-ide-text rounded-md border border-ide-border hover:bg-ide-surfaceHover text-sm transition active:scale-[0.98]"
          title="Stop Sketch"
          aria-label="Stop Sketch"
        >
          <Square size={14} />
          <span>Stop</span>
        </button>

        <div className="h-5 w-px bg-ide-border mx-1" />

        {/* Solo/Group Mode Toggle */}
        <ModeToggle onGroupClick={() => setShowRoomPicker(true)} />

        <div className="h-5 w-px bg-ide-border mx-1" />

        {/* Quick actions */}
        <button
          type="button"
          onClick={() => setShowMySketches(true)}
          className="p-1.5 text-ide-textMuted rounded-md hover:bg-ide-surface hover:text-ide-text transition"
          title="My Sketches (Cloud Storage)"
          aria-label="My Sketches"
        >
          <FolderOpen size={16} />
        </button>

        <button
          type="button"
          onClick={handleSync}
          className="p-1.5 text-ide-textMuted rounded-md hover:bg-ide-surface hover:text-ide-text transition disabled:opacity-40 disabled:cursor-not-allowed"
          title="Sync to p5js.org"
          aria-label="Sync to p5.js Editor"
          disabled={!isAuthenticated}
        >
          <Upload size={16} />
        </button>

        <button
          type="button"
          onClick={handleDownload}
          className="p-1.5 text-ide-textMuted rounded-md hover:bg-ide-surface hover:text-ide-text transition"
          title="Download Sketch"
          aria-label="Download Sketch"
        >
          <Download size={16} />
        </button>

        <button
          type="button"
          onClick={() => setShowPublish(true)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-ide-success/10 text-ide-success border border-ide-success/30 rounded-md hover:bg-ide-success/20 transition text-sm font-medium active:scale-[0.98]"
          title="Publish to Gallery"
          aria-label="Publish to Gallery"
          data-tour="publish"
        >
          <Globe size={14} />
          <span className="hidden lg:inline">Publish</span>
        </button>

        <a
          href="/gallery/"
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-ide-surface text-ide-textMuted border border-ide-border rounded-md hover:bg-ide-surfaceHover hover:text-ide-text transition text-sm active:scale-[0.98]"
          title="Browse Gallery"
          aria-label="Browse Gallery"
        >
          <Image size={14} />
          <span className="hidden lg:inline">Gallery</span>
        </a>

        <a
          href="/research"
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-ide-surface text-ide-textMuted border border-ide-border rounded-md hover:bg-ide-surfaceHover hover:text-ide-text transition text-sm active:scale-[0.98]"
          title="Research"
          aria-label="Research"
        >
          <BookOpen size={14} />
          <span className="hidden lg:inline">Research</span>
        </a>

        <a
          href="/docs"
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-ide-surface text-ide-textMuted border border-ide-border rounded-md hover:bg-ide-surfaceHover hover:text-ide-text transition text-sm active:scale-[0.98]"
          title="User Guide"
          aria-label="User Guide"
        >
          <BookOpen size={14} />
          <span className="hidden lg:inline">Docs</span>
        </a>

        <div className="h-5 w-px bg-ide-border mx-1" />

        {/* Version selector - compact */}
        <label htmlFor="p5-version-select" className="sr-only">p5.js Version</label>
        <select
          id="p5-version-select"
          value={p5Version}
          onChange={(e) => {
            setP5Version(e.target.value)
            showToast(`Switched to p5.js v${e.target.value}`, 'success')
          }}
          className="px-2 py-1 bg-ide-bg text-ide-textMuted rounded-md border border-ide-border hover:border-ide-borderHover transition text-xs"
          title="Select p5.js Library Version"
          aria-label="Select p5.js Library Version"
        >
          {p5Versions.map(v => (
            <option key={v} value={v}>v{v}</option>
          ))}
        </select>

        <div className="h-5 w-px bg-ide-border mx-1" />

        {/* AI Toggle */}
        <button
          type="button"
          onClick={toggleAiPanel}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm transition active:scale-[0.98] ${
            showAiPanel 
              ? 'bg-ide-accent text-white' 
              : 'bg-ide-surface text-ide-textMuted border border-ide-border hover:bg-ide-surfaceHover hover:text-ide-text'
          }`}
          title="Toggle AI Assistant"
          aria-label="Toggle AI Assistant"
        >
          <Sparkles size={14} />
          <span>AI</span>
        </button>

        {/* Command palette hint */}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('openCommandPalette'))}
          className="hidden md:flex items-center gap-1.5 px-2 py-1 text-ide-textDim rounded-md border border-ide-border hover:bg-ide-surface hover:text-ide-textMuted transition text-xs"
          title="Command Palette (Ctrl+K)"
          aria-label="Open Command Palette"
        >
          <Command size={12} />
          <span>K</span>
        </button>

        <div className="h-5 w-px bg-ide-border mx-1" />

        {/* Changelog - icon only */}
        <a
          href="/changelog"
          onClick={(e) => {
            e.preventDefault()
            onChangelogClick()
          }}
          className="p-1.5 text-ide-textMuted rounded-md hover:bg-ide-surface hover:text-ide-text transition flex items-center justify-center"
          title="Changelog"
          aria-label="Changelog"
        >
          <History size={16} />
        </a>

        {/* About - icon only */}
        <a
          href="/about"
          onClick={(e) => {
            e.preventDefault()
            onAboutClick()
          }}
          className="p-1.5 text-ide-textMuted rounded-md hover:bg-ide-surface hover:text-ide-text transition flex items-center justify-center"
          title="About"
          aria-label="About"
        >
          <Info size={16} />
        </a>

        {/* Help / Tour - icon only */}
        <button
          type="button"
          onClick={onHelpClick}
          className="p-1.5 text-ide-textMuted rounded-md hover:bg-ide-surface hover:text-ide-text transition flex items-center justify-center"
          title="Start Tour"
          aria-label="Start Tour"
          data-tour="help-button"
        >
          <HelpCircle size={16} />
        </button>

        {/* User */}
        <button
          type="button"
          onClick={onAuthClick}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-ide-surface text-ide-text rounded-md border border-ide-border hover:bg-ide-surfaceHover transition text-sm"
          aria-label={isAuthenticated ? 'Account Settings' : 'Login to p5.js'}
        >
          <User size={14} />
          <span className="hidden sm:inline">{isAuthenticated ? user?.username || 'Account' : 'Login'}</span>
        </button>
      </div>

      {/* Sketch Browser Modal */}
      {showBrowser && <SketchBrowser onClose={() => setShowBrowser(false)} />}
      
      {/* Publish Modal */}
      {showPublish && <PublishModal onClose={() => setShowPublish(false)} />}
      
      {/* Template Modal */}
      {showTemplates && <TemplateModal onClose={() => setShowTemplates(false)} />}
      
      {/* Social Room Picker Modal */}
      {showRoomPicker && <SocialRoomPicker onClose={() => setShowRoomPicker(false)} />}
      
      {/* My Sketches Panel */}
      {showMySketches && <MySketchesPanel onClose={() => setShowMySketches(false)} />}
    </header>
  )
}
